import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, catchError, EMPTY, filter, from, switchMap, take, throwError } from 'rxjs';
import { SessionMonitorService } from '@v1/core/oauth/session-monitor.service';
import { isOAuthProtectedResource } from '@v1/core/oauth/oauth-resource-server.config';

type RefreshResult = { token?: string; error?: unknown; redirecting?: boolean };
let refreshing = false;
const refreshResult = new BehaviorSubject<RefreshResult | null>(null);

export const oauthErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const oauth = inject(OAuthService);
  const session = inject(SessionMonitorService);
  return next(request).pipe(catchError((error: HttpErrorResponse) => {
    if (error.status !== 401 || request.url.includes('/op/token') || request.url.includes('/op/logout') || !isOAuthProtectedResource(request.url)) return throwError(() => error);
    return refreshAndRetry(request, next, oauth, session, error);
  }));
};

function refreshAndRetry(request: HttpRequest<unknown>, next: HttpHandlerFn,
  oauth: OAuthService, session: SessionMonitorService, original: HttpErrorResponse) {
  if (refreshing) return refreshResult.pipe(
    filter((result): result is RefreshResult => result !== null), take(1),
    switchMap(result => result.token
      ? retryWithBearer(request, next, result.token, session)
      : result.redirecting ? EMPTY : throwError(() => result.error ?? original)),
  );
  if (!oauth.getRefreshToken()) {
    if (!oauth.getAccessToken()) return throwError(() => original);
    session.closeLocal();
    return EMPTY;
  }
  refreshing = true;
  refreshResult.next(null);
  // Catch only the token exchange. A 401 from the retried API is not another refresh attempt.
  return from(session.refreshAccessToken()).pipe(
    catchError((error: unknown) => {
      refreshing = false;
      if (isDefinitiveRefreshFailure(error)) {
        refreshResult.next({ redirecting: true });
        session.closeLocal();
        return EMPTY;
      }
      refreshResult.next({ error });
      session.error.set('No se pudo renovar la sesión. Reintenta cuando vuelva la conexión.');
      return throwError(() => error);
    }),
    switchMap((token) => {
      refreshing = false;
      if (!token) {
        refreshResult.next({ redirecting: true });
        session.closeLocal();
        return EMPTY;
      }
      refreshResult.next({ token });
      return retryWithBearer(request, next, token, session);
    }),
  );
}

function retryWithBearer(request: HttpRequest<unknown>, next: HttpHandlerFn,
  token: string, session: SessionMonitorService) {
  return next(withBearer(request, token)).pipe(catchError((error: HttpErrorResponse) => {
    if (error.status === 401) { session.closeLocal(); return EMPTY; }
    return throwError(() => error);
  }));
}

function withBearer(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
}

function isDefinitiveRefreshFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 401 ||
    error.status === 400 && (error.error?.error === 'invalid_grant' ||
      error.error?.error === 'session_revoked'));
}
